import moment from 'moment';

export function deepMerge(...objects: object[]) {
  const isObject = (obj: any) => obj && typeof obj === 'object';

  function deepMergeInner(target: object, source: object) {
    Object.keys(source).forEach((key: string) => {
      const targetValue = target[key];
      const sourceValue = source[key];

      if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
        target[key] = targetValue.concat(sourceValue);
      } else if (isObject(targetValue) && isObject(sourceValue)) {
        target[key] = deepMergeInner(Object.assign({}, targetValue), sourceValue);
      } else {
        target[key] = sourceValue;
      }
    });

    return target;
  }

  if (objects.length < 2) {
    throw new Error('deepMerge: this function expects at least 2 objects to be provided');
  }

  if (objects.some(object => !isObject(object))) {
    throw new Error('deepMerge: all values should be of type "object"');
  }

  const target = objects.shift();
  let source: object;

  while (source = objects.shift()) {
    deepMergeInner(target, source);
  }

  return target;
}

export function deepCopy(args: Partial<any>) {
  return JSON.parse(JSON.stringify(args));
}

export function removeEmptyProperty(obj: object) {
  Object.keys(obj).forEach(key => obj[key] == null && delete obj[key]);
}

export const conversaoFormatoData = (dataString: string): string => {
  const tratamento = dataString.split('-');

  if (tratamento.length < 3) {
    return dataString;
  }

  const novaStringData = tratamento[2] + '/' + tratamento[1] + '/' + tratamento[0];

  return novaStringData;
};

export const conversaoFormatoPeriodo = (dataString: string): string => {
  const data = moment(dataString);

  if (!data.isValid()) {
    return dataString;
  }

  return data.format('DD/MM/YYYY [às] HH:mm');
};

const HASHES_BLOQUEADOS = ['iss'];

export const filtrarFragmento = (hash: string, blockedHashes: string[] = HASHES_BLOQUEADOS): string => {
  if (!hash) {
    return '';
  }

  const hashSemCerquilha = hash.startsWith('#') ? hash.substring(1) : hash;

  if (!hashSemCerquilha) {
    return '';
  }

  const partesFiltradas = hashSemCerquilha
    .split('&')
    .filter((parte) => {
      const chave = parte.split('=')[0];

      return !blockedHashes.includes(chave);
    })
    .filter((parte) => parte !== '');

  return partesFiltradas.length > 0 ? `#${partesFiltradas.join('&')}` : '';
};

export const deveIgnorarFragmento = (hash: string, blockedHashes: string[] = HASHES_BLOQUEADOS): boolean => {
  if (!hash) {
    return false;
  }

  return filtrarFragmento(hash, blockedHashes) !== hash;
};

export const normalizarUrlSemProtocolo = (url: string): string => {
  const indexUrl = url.indexOf('://');
  const urlSemProtocolo = indexUrl > -1 ? url.substring(indexUrl + 3) : url;
  const indexUrlParams = urlSemProtocolo.indexOf('?');
  const indexUrlHash = urlSemProtocolo.indexOf('#');

  let indexFimUrl = urlSemProtocolo.length;
  let fragmentoFiltrado = '';

  if (indexUrlParams > -1) {
    indexFimUrl = indexUrlParams;
  }

  if (indexUrlHash > -1) {
    fragmentoFiltrado = filtrarFragmento(urlSemProtocolo.substring(indexUrlHash));

    if (indexUrlParams === -1) {
      indexFimUrl = indexUrlHash;
    } else {
      indexFimUrl = Math.min(indexFimUrl, indexUrlHash);
    }
  }

  const urlNormalizada = `${urlSemProtocolo.substring(0, indexFimUrl)}${fragmentoFiltrado}`;

  if (urlNormalizada[urlNormalizada.length - 1] === '/') {
    return urlNormalizada.substr(0, urlNormalizada.length - 1);
  }

  return urlNormalizada;
};

export const decodificarComponenteUrl = (componente: string | null): string | null => {
  if (!componente) {
    return componente;
  }

  try {
    return decodeURIComponent(componente);
  } catch {
    return componente;
  }
};

export const removerFragmentoDaUrlNavegador = (): void => {
  const hashFiltrado = filtrarFragmento(window.location.hash);

  if (hashFiltrado === window.location.hash) {
    return;
  }

  const novaUrl = `${window.location.pathname}${window.location.search}${hashFiltrado}`;
  window.history.replaceState(window.history.state, document.title, novaUrl);
};

export const extrairNomeContentDisposition = (contentDisposition: string): string | null => {
  if (!contentDisposition) {
    return null;
  }

  const filenameUtf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (filenameUtf8 && filenameUtf8[1]) {
    return decodeURIComponent(filenameUtf8[1].replace(/^"|"$/g, '').trim());
  }

  const filename = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return filename && filename[1] ? filename[1].trim() : null;
};

export const montarNomeArquivoComExtensao = (nome: string, extensao?: string, fallback = 'arquivo'): string => {
  const nomeBase = nome || fallback;
  const extensaoNormalizada = (extensao || '').replace(/^\./, '');

  if (!extensaoNormalizada || new RegExp(`\\.${extensaoNormalizada}$`, 'i').test(nomeBase)) {
    return nomeBase;
  }

  return `${nomeBase}.${extensaoNormalizada}`;
};

export const resolverNomeArquivoDownload = (
  contentDisposition: string,
  nome: string,
  extensao?: string,
  fallback = 'arquivo'
): string => {
  return extrairNomeContentDisposition(contentDisposition) || montarNomeArquivoComExtensao(nome, extensao, fallback);
};
